import { Injectable } from '@angular/core'
import { Location } from '@angular/common'
import { Observable } from 'rxjs/Observable'
import { HttpClient } from '@angular/common/http'
import { BehaviorSubject } from 'rxjs/BehaviorSubject'
import { environment } from '../../../environments/environment'
import { PageEvent } from '@angular/material'
import { UserModel } from '../models/user.model'
import { UserResponse } from '../models/user-response'
import { LoginMethods } from '../models/login-methods.model'
import 'rxjs/add/operator/map'
import 'rxjs/add/operator/shareReplay'
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router'
import { ResourceLoadingService } from './resource-loading.service'

@Injectable()
export class UserService implements Resolve<Observable<any>> {
  private readonly _old_url = environment.base_url + '/v1/support/users'
  private readonly url = environment.base_url + '/v2/support/users'
  private readonly _users_url = environment.base_url + '/v1/user'
  private readonly _password_url = environment.base_url + '/v1/password'
  private readonly _token_reset_url = environment.base_url + '/v2/activation/request-token'

  private filters: any

  users: UserResponse
  user: UserModel

  onUserChanged: BehaviorSubject<any> = new BehaviorSubject(undefined)
  onUsersChanged: BehaviorSubject<UserResponse> = new BehaviorSubject<UserResponse>(undefined)

  constructor(
    private http: HttpClient,
    private router: Router,
    private location: Location,
    private _loadingService: ResourceLoadingService,
  ) {
    this.filters = {
      paginated: true,
    }
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    const userId = route.params['userId']

    if (route.params['searchQuery']) {
      this.filters.search = route.params['searchQuery']
    }

    if (userId) {
      return this.get(userId)
    } else {
      return this.browse()
    }
  }

  /**
   *
   */
  browse(): Observable<UserResponse> {
    this._loadingService.loading(true)
    return this.http
      .get<UserResponse>(this.url, { params: this.filters })
      .map(response => {
        this.users = new UserResponse(response)
        this._loadingService.loading(false)
        this.onUsersChanged.next(this.users)
        return this.users
      })
  }

  /**
   * Update a User
   * @param {UserModel} user
   * @returns {Observable<UserModel>}
   */
  update(user: UserModel): Observable<UserModel> {
    return this.http.put<UserModel>(`${this._old_url}/${user.id}`, user)
  }

  /**
   *
   * @param {number} userId
   */
  get(userId: number): Observable<UserModel> {
    return this.http.get<UserModel>(`${this.url}/${userId}`).map(user => {
      this.user = new UserModel(user)
      this.onUserChanged.next(this.user)
      return this.user
    })
  }

  /**
   * Resend activation link
   * @param {UserModel} user
   * @returns {Observable<UserModel>}
   */
  resendActivation(user: UserModel): Observable<UserModel> {
    return this.http.post<UserModel>(`${this._users_url}/${user.id}/resend-activation`, {})
  }

  /**
   * Resend invite link
   * @param {UserModel} user
   * @returns {Observable<UserModel>}
   */
  resendInvite(user: UserModel): Observable<any> {
    return this.http
      .get(`${this.url}/${user.id}/request-token`)
      .switchMap(token => {
        return this.http.post(`${this._token_reset_url}`, { token: token['token'] })
      })
      .shareReplay()
  }

  /**
   * Resend activation link
   * @param {UserModel} user
   * @returns {Observable<UserModel>}
   */
  passwordReset(user: UserModel): Observable<UserModel> {
    return this.http.post<UserModel>(`${this._password_url}/request-reset`, { email: user.email })
  }

  /**
   * (un)Blocks a user
   * @param {UserModel} user
   * @returns {Observable<UserModel>}
   */
  block(user: UserModel): Observable<UserModel> {
    return this.http.patch<UserModel>(`${this.url}/${user.id}/block`, {})
  }

  /**
   * Returns all available login methods for the given user
   * @param user
   */
  retrieveInlogLinkAndPassword(user: UserModel): Observable<LoginMethods> {
    return this.http.post<LoginMethods>(`${this.url}/${user.id}/magic-link/login`, {})
  }

  getSearch(): any {
    return this.filters.search
  }

  changeSearch(search: string) {
    this.filters.search = search
    this.browse().subscribe()
    this.unSetCurrentUser()
  }

  changeFilter(event?: PageEvent) {
    this.filters.page = event.pageIndex + 1
    this.filters.limit = event.pageSize
    this.browse().subscribe()
  }

  changeRole(role) {
    this.filters.role = role
    this.browse().subscribe()
  }

  changeState(state) {
    this.filters.status = state
    this.browse().subscribe()
  }

  clearFilter() {
    this.filters = {
      paginated: true,
    }
    this.browse().subscribe()
  }

  /**
   *
   * @param {UserModel} user
   */
  setCurrentUser(user: UserModel) {
    this.router.navigate(['users/' + user.id])
  }

  /**
   * setting the current administration
   * @param administration
   */
  unSetCurrentUser() {
    this.user = undefined
    this.router.navigate(['users'])
  }
}



// WEBPACK FOOTER //
// ./src/app/main/services/user.service.ts